import { FC, memo, useCallback, useMemo } from 'react';
import { mainColors } from '../../styling/theme';
import { formatDateForCheckingState } from '../../utilities/dateFormatters';
import { BASE_URL } from '../../utilities/apiConstants';
import {
  findRecentReportDateFromNames,
  getCachedReportDates,
  parseReportNameToDateString,
} from '../../utilities/parseReportDates';
import ReportDatePicker from '../selects/ReportDatePicker.component';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { activeDateSelector } from '../../redux/pages/selectors';
import { Typography } from '@mui/material';

interface GeneralReportDatePickerProps {
  reportNames: string[];
  reportUrl: string;
  fundId: string;
  customGetDatesFromReportNamesAsStringsFunction?: (
    reportNames: string[],
  ) => string[];
  customFindReportnameFromDate?: (
    date: Date | null,
    reportNames: string[],
  ) => string | null;
}

function openInNewTab(url: string) {
  return async () => {
    const win = window.open(url, '_blank');
    if (win) {
      win.focus();
      return true;
    } else {
      return false;
    }
  };
}

function findReportnameFromDate(
  date: Date | null,
  reportNames: string[],
): string | null {
  if (!date) return null;
  const dateString = formatDateForCheckingState(date);
  let foundReportName = '';
  reportNames.forEach((reportName: string) => {
    if (dateString === parseReportNameToDateString(reportName)) {
      foundReportName = reportName;
    }
  });
  return foundReportName;
}

const GeneralReportDatePicker: FC<GeneralReportDatePickerProps> = (props) => {
  const activeDate = useSelector(activeDateSelector);

  const { reportNames, reportUrl, fundId } = props;

  const parseOneReportName = useCallback(
    (reportName: string) => {
      if (props.customGetDatesFromReportNamesAsStringsFunction) {
        return props.customGetDatesFromReportNamesAsStringsFunction([
          reportName,
        ])[0];
      }
      return parseReportNameToDateString(reportName);
    },
    [props.customGetDatesFromReportNamesAsStringsFunction],
  );

  const resolveDatesToInclude = useCallback(() => {
    return getCachedReportDates(
      reportNames,
      props.customGetDatesFromReportNamesAsStringsFunction ??
        ((names) => names.map(parseReportNameToDateString)),
    );
  }, [reportNames, props.customGetDatesFromReportNamesAsStringsFunction]);

  const selectedReportDate = useMemo(
    () =>
      findRecentReportDateFromNames(
        reportNames,
        dayjs(activeDate).format('YYYY-MM-DD'),
        parseOneReportName,
      ),
    [reportNames, activeDate, parseOneReportName],
  );

  const dateHandler = (date: Date | null) => {
    if (!date) return;
    const reportName = props.customFindReportnameFromDate
      ? props.customFindReportnameFromDate(date, reportNames)
      : findReportnameFromDate(date, reportNames);
    if (reportName) {
      const openReport = openInNewTab(
        `${BASE_URL}${reportUrl}/${reportName}/${fundId}`,
      );
      openReport();
    }
  };

  return reportNames?.length ? (
    <div>
      <ReportDatePicker
        key={'date-picker'}
        title={'Select Report:'}
        handler={dateHandler}
        resolveDatesToInclude={resolveDatesToInclude}
        selectedDate={dayjs(selectedReportDate)}
      />
    </div>
  ) : (
    <Typography
      style={{ fontSize: '0.88rem', color: mainColors.mainBlue }}
      variant="h5"
    >
      No reports available
    </Typography>
  );
};

export default memo(GeneralReportDatePicker);
